import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import DialogShell from '../kit/DialogShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '../errors';

// Edit an appointment's title, notes and address (ticket 189). Saved to
// GoHighLevel first, then the mirror (PATCH /v2/appointments/:id/details), like
// create: on any error the dialog stays open with the API's message and
// nothing has changed. Only the fields that differ are sent.

const TITLE_MAX = 120;
const NOTES_MAX = 5000;
const ADDRESS_MAX = 300;

// The fields of `form` that differ from the appointment (trimmed; "" = cleared).
export function changedDetails(appointment, form) {
  const out = {};
  ['title', 'notes', 'address'].forEach((k) => {
    const next = (form[k] || '').trim();
    if (next !== (appointment?.[k] || '').trim()) out[k] = next;
  });
  return out;
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function DetailsForm({ appointment, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: appointment?.title || '',
    notes: appointment?.notes || '',
    address: appointment?.address || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const changes = changedDetails(appointment, form);
  const blankTitle = 'title' in changes && !changes.title;
  const canSubmit = !saving && Object.keys(changes).length > 0 && !blankTitle;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSaving(true);
    try {
      const { data } = await api.patch(`/v2/appointments/${appointment.appointment_id}/details`, changes);
      onSaved?.(data);
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the details.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="details-form">
      <Field label="Title" htmlFor="details-title">
        <Input id="details-title" value={form.title} maxLength={TITLE_MAX} onChange={set('title')} data-testid="details-title" />
      </Field>
      <Field label="Address" htmlFor="details-address">
        <Input id="details-address" value={form.address} maxLength={ADDRESS_MAX} onChange={set('address')} placeholder="Where it takes place" data-testid="details-address" />
      </Field>
      <Field label="Notes" htmlFor="details-notes">
        <Textarea id="details-notes" rows={5} value={form.notes} maxLength={NOTES_MAX} onChange={set('notes')} data-testid="details-notes" />
      </Field>
      {error && <p role="alert" className="text-sm text-red-600" data-testid="details-error">{error}</p>}
      <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="details-cancel">Cancel</Button>
        <Button type="submit" disabled={!canSubmit} data-testid="details-submit">
          {saving && <Loader2 className="animate-spin" size={14} />}
          Save
        </Button>
      </div>
    </form>
  );
}

export default function DetailsDialog({ open, appointment, onClose, onSaved }) {
  return (
    <DialogShell
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Appointment details"
      description="Saved to GoHighLevel first, then here."
      className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto sm:w-full"
    >
      <DetailsForm appointment={appointment} onClose={onClose} onSaved={onSaved} />
    </DialogShell>
  );
}
